import { useState } from "react";
import { testMicrosoftConnection } from "services/company.service";

/** `reason` is the server's code, or REQUEST_FAILED when the server could not be asked. */
export type ConnectionCheckResult =
  | { ok: true }
  | { ok: false; reason: string };

type CheckVerdict = { tone: "ok" | "warn" | "error"; textKey: string };

// What the person reads for each way the check can fail, and how serious it is.
// A reason this table does not know (a newer server) reads like REQUEST_FAILED.
const FAILURE_VERDICTS: Record<string, CheckVerdict> = {
  NOT_CONFIGURED: { tone: "warn", textKey: "settings_ms_check_not_configured" },
  SECRET_EXPIRED: { tone: "error", textKey: "settings_ms_check_expired" },
  SECRET_INVALID: { tone: "error", textKey: "settings_ms_check_invalid" },
  AUTH_FAILED: { tone: "error", textKey: "settings_ms_check_auth_failed" },
  TIMEOUT: { tone: "warn", textKey: "settings_ms_check_timeout" },
  FOLDER_NOT_FOUND: { tone: "error", textKey: "settings_ms_check_no_folder" },
  FOLDER_UNREACHABLE: {
    tone: "warn",
    textKey: "settings_ms_check_folder_error",
  },
  REQUEST_FAILED: { tone: "warn", textKey: "settings_ms_check_request_failed" },
};

export function checkVerdict(result: ConnectionCheckResult): CheckVerdict {
  if (result.ok) return { tone: "ok", textKey: "settings_ms_check_ok" };
  return FAILURE_VERDICTS[result.reason] ?? FAILURE_VERDICTS.REQUEST_FAILED;
}

/**
 * Asks the server to try the saved Microsoft app (and the company folder in
 * OneDrive). `result` stays empty until a check has run.
 */
export function useMicrosoftConnectionCheck(companyId: string) {
  const [result, setResult] = useState<ConnectionCheckResult | null>(null);
  const [checking, setChecking] = useState(false);

  const run = async () => {
    setChecking(true);
    try {
      const answer = await testMicrosoftConnection(companyId);
      setResult(
        answer.ok ? { ok: true } : { ok: false, reason: answer.reason }
      );
    } catch {
      setResult({ ok: false, reason: "REQUEST_FAILED" });
    } finally {
      setChecking(false);
    }
  };

  return { result, checking, run };
}

export type MicrosoftConnectionCheck = ReturnType<
  typeof useMicrosoftConnectionCheck
>;
